import type { ReactNode, RefObject } from 'react';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import {
  hotelOfferPagesNoticeKind,
  type HotelResultsProps,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { HotelPlannerBlock } from '@voll-intelligence/genui-contract';
import type { createHotelPlannerView } from './hotel_planner_view.js';
import { HotelMapFullscreen, type HotelMapFullscreenHost } from './hotel_map_fullscreen.js';
import type { ExpiredSearchRetry } from './use_expired_search_retry.js';
import type { HotelPanelList } from './use_hotel_panel_list.js';

/*
 * O mapa cheio do organismo de resultados, uma tela em diálogo — saiu de
 * `hotel_results_organism.tsx` pelo teto de linhas. O aberto/fechado continua
 * lá (`useResultsDialogLifecycle`): este arquivo só recebe a fatia que usa,
 * tipada estruturalmente para não importar o organismo de volta.
 */

/** As props que o organismo da conversa deriva para o portal. `dispatch` é
 *  opcional no mapa cheio (item e, parte 2): `exactOptionalPropertyTypes` proíbe
 *  passar a chave com valor `undefined` explícito, por isso o spread condicional
 *  isolado numa função — o mesmo motivo do `hotel_map_fullscreen.tsx`. Na
 *  conversa a gaveta de detalhe abre DENTRO do mapa (`closeOnOpenDetails`
 *  falso). `expiredRetry` passa por aqui, e não como prop solta no JSX do
 *  organismo, pelo teto de linhas dele. */
export function fullscreenPortalProps(
  dispatch: GenUiDispatch | undefined,
  expiredRetry: ExpiredSearchRetry,
): { dispatch?: GenUiDispatch; closeOnOpenDetails: boolean; expiredRetry: ExpiredSearchRetry } {
  return { closeOnOpenDetails: false, expiredRetry, ...(dispatch ? { dispatch } : {}) };
}

/** A coleta inicial da lista está no ar — a MESMA régua do aviso de espera da
 *  página de resultados (`tone: 'loading'`): sem base na tela, o "buscar nesta
 *  área" não tem área a reofertar e não arma. */
function collectingList(page: HotelPanelList['page'], expiredRetry: ExpiredSearchRetry): boolean {
  const kind = hotelOfferPagesNoticeKind(page, expiredRetry);
  return kind === 'loadingAll' || kind === 'staleRetrying';
}

/** As props que a lista cheia e o mapa cheio compartilham. */
export type ResultsDialogSharedProps = {
  readonly view: ReturnType<typeof createHotelPlannerView>;
  /** A lista COMPLETA, uma só para as duas superfícies (ver `useHotelPanelList`):
   *  o mapa cheio abre por cima da lista e tem de mostrar os mesmos hotéis. */
  readonly list: HotelPanelList;
  readonly ui: HotelPlannerBlock;
  readonly search: HotelResultsProps['search'];
  readonly locked: boolean;
  readonly onOpenDetails: (hotelId: string) => void;
};

/** A fatia do ciclo de vida que as telas em diálogo usam. */
export type ResultsDialogLifecycleSlice = {
  readonly expanded: boolean;
  readonly dialogRef: RefObject<HTMLDivElement | null>;
  readonly close: () => void;
};

export function HotelMapDialog({
  host,
  lifecycle,
  view,
  list,
  ui,
  search,
  locked,
  dispatch,
  closeOnOpenDetails,
  expiredRetry,
  onOpenDetails,
  title,
  nested,
}: ResultsDialogSharedProps & {
  readonly host: HotelMapFullscreenHost;
  readonly lifecycle: ResultsDialogLifecycleSlice;
  readonly dispatch?: GenUiDispatch;
  /** Em que pé está a busca vencida — entra na régua do `searchAreaPending`. */
  readonly expiredRetry: ExpiredSearchRetry;
  /** Na bancada a Página do Hotel é uma CAMADA do host sobre os resultados,
   *  que seguem montados (#3217): este diálogo ficaria por cima dela, então
   *  fecha antes de abrir o detalhe. No chat a gaveta de detalhe abre DENTRO
   *  dele (`nested`) e ele fica onde está. */
  readonly closeOnOpenDetails: boolean;
  readonly title: string;
  /** As camadas abertas por cima do mapa (a gaveta de detalhes). */
  readonly nested?: ReactNode;
}): ReactNode {
  if (!lifecycle.expanded) return null;
  return (
    <ScreenDialog
      label={title}
      onClose={lifecycle.close}
      popupRef={lifecycle.dialogRef}
      className="bg-card"
      attributes={{ 'data-hotel-map-fullscreen-dialog': 'true' }}
    >
      <HotelMapFullscreen
        host={host}
        map={view.map}
        offers={list.views}
        sourceOffers={list.sources}
        nights={ui.nights}
        place={ui.placeDetails}
        copy={view.resultsCopy}
        search={search}
        selectedHotelId={ui.selectedOfferId}
        locked={locked}
        /* Mesma régua da página: total do fornecedor além dos carregados ⇒
             contagens parciais e cortes de servidor clicáveis em zero. */
        countsPartial={list.total !== undefined && list.total > list.sources.length}
        filters={list.query.filters}
        onFiltersChange={(next) => {
          // A ordem NÃO é eixo do mapa cheio: preservá-la é o que impede o
          // corte feito ali de resetar o ordenador da lista atrás dele.
          list.setQuery({ filters: next, order: list.query.order });
        }}
        hasMore={list.page.hasMore}
        loadingMore={list.page.phase === 'loadingMore'}
        onLoadMore={list.page.loadMore}
        loadMoreResetToken={list.page.requestKey}
        searchAreaPending={collectingList(list.page, expiredRetry)}
        {...(dispatch ? { dispatch } : {})}
        onClose={lifecycle.close}
        onOpenDetails={(hotelId) => {
          if (closeOnOpenDetails) lifecycle.close();
          onOpenDetails(hotelId);
        }}
      />
      {nested}
    </ScreenDialog>
  );
}
